import { useEffect, useState } from "react";
import api, { fmtMoney, fmtQty } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { PageHeader, Empty } from "../components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DollarSign, Package, Layers, Calendar, TrendingUp } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className="text-primary" />
      <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-3xl font-black tabular-nums leading-none">{value}</div>
  </div>
);

export default function ConsumptionByArea() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [growth, setGrowth] = useState(1.05);
  const [forecast, setForecast] = useState(null);
  const [forecastMode, setForecastMode] = useState("area"); // area | family
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    api.get("/reports/consumption-by-area", { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => { setData({ totals: { grand_qty_out: 0, grand_cost_total: 0, area_count: 0, avg_daily_cost: 0 }, by_area: [], top_equipments: [] }); setError(e?.response?.data?.detail || "Error al cargar consumo"); });
    api.get("/reports/purchase-forecast", {
      params: { window_days: days, forecast_days: days, growth_factor: growth },
    }).then((r) => setForecast(r.data)).catch(() => setForecast(null));
  }, [days, growth]);

  if (!data) return <div className="text-xs text-muted-foreground p-6" data-testid="cba-loading">Cargando…</div>;

  const T = data.totals;
  const hasNoData = !data.top_equipments.length && !data.by_area.length && T.grand_cost_total === 0;

  return (
    <div data-testid="consumption-by-area-page">
      <PageHeader title={t("cba.title")} subtitle={t("cba.subtitle")}>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="cba-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d} {t("rb.days")}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {/* Helper banner */}
      <div className="mb-4 border-l-4 border-primary bg-primary/5 p-3 text-[11px]" data-testid="cba-helper">
        <div className="font-bold uppercase tracking-widest mb-1">¿Qué muestra este panel?</div>
        <div className="text-muted-foreground">
          Consumo real de refacciones y materiales del almacén asignado a Órdenes de Trabajo (Cardex tipo <b>MNT</b>) en los últimos {days} días,
          agrupado por <b>máquina</b> y por <b>área</b>. Los totales excluyen consumos operativos sin OT. El pronóstico
          proyecta el costo esperado del próximo periodo aplicando un factor de crecimiento.
        </div>
      </div>

      {error && (
        <div className="mb-4 border border-red-300 bg-red-50 dark:bg-red-950/20 p-3 text-xs text-red-800" data-testid="cba-error">
          <b>Error:</b> {error}
        </div>
      )}

      {hasNoData && !error && (
        <div className="mb-4 border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-xs text-amber-800" data-testid="cba-no-data">
          <b>Aún no hay datos.</b> Empieza registrando consumo en una OT (materiales) para ver este reporte con información real.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon={DollarSign} label={t("cba.grandCost")} value={fmtMoney(T.grand_cost_total)} testid="cba-kpi-cost" />
        <StatCard icon={Package} label={t("cba.grandQty")} value={fmtQty(T.grand_qty_out)} testid="cba-kpi-qty" />
        <StatCard icon={Layers} label={t("cba.areas")} value={T.area_count} testid="cba-kpi-areas" />
        <StatCard icon={Calendar} label={t("cba.avgDaily")} value={fmtMoney(T.avg_daily_cost)} testid="cba-kpi-avg" />
      </div>

      {/* Consumo por Máquina (vista primaria) */}
      {data.top_equipments.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-2">Consumo por máquina ({t("cba.topEquipment")})</div>
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr><th>Equipo</th><th>{t("cba.area")}</th><th className="num">{t("cba.movements")}</th><th className="num">Qty</th><th className="num">{t("cba.grandCost")}</th></tr></thead>
            <tbody>
              {data.top_equipments.map((e) => (
                <tr key={e.equipment_code} data-testid={`cba-eq-${e.equipment_code}`}>
                  <td><span className="font-bold tabular-nums">{e.equipment_code}</span> · <span className="text-[11px]">{e.equipment_name}</span></td>
                  <td className="text-[10px] uppercase text-muted-foreground">{e.area}</td>
                  <td className="num tabular-nums">{e.movements}</td>
                  <td className="num tabular-nums">{fmtQty(e.qty_out)}</td>
                  <td className="num tabular-nums font-bold">{fmtMoney(e.cost_total)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {data.by_area.length === 0 ? null : (
        <div className="space-y-3 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-widest">{t("cba.area")}s</div>
          {data.by_area.map((a, i) => {
            const pct = T.grand_cost_total > 0 ? (a.cost_total / T.grand_cost_total) * 100 : 0;
            return (
              <div key={a.area} className="border" data-testid={`cba-area-${i}`}>
                <div className="p-3 flex items-center justify-between border-b">
                  <div>
                    <div className="font-black text-sm uppercase">{a.area}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {a.movements} {t("cba.movements").toLowerCase()} · {a.equipments_count} {t("cba.equipments").toLowerCase()} · Qty {fmtQty(a.qty_out)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-black tabular-nums text-primary">{fmtMoney(a.cost_total)}</div>
                    <div className="text-[10px] tabular-nums text-muted-foreground">{pct.toFixed(1)}%</div>
                  </div>
                </div>
                <div className="w-full h-1.5 bg-muted">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }}></div>
                </div>
                {a.top_items.length > 0 && (
                  <div className="p-3">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t("cba.topItems")}</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {a.top_items.map((it) => (
                          <tr key={it.item_number} className="border-b last:border-0">
                            <td className="py-1 font-bold tabular-nums w-40">{it.item_number}</td>
                            <td className="py-1 text-[11px] truncate">{it.description}</td>
                            <td className="py-1 text-right tabular-nums w-24">{fmtQty(it.qty_out)}</td>
                            <td className="py-1 text-right tabular-nums font-bold w-28">{fmtMoney(it.cost_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pronóstico próximo trimestre */}
      {forecast && (
        <div className="border bg-card p-4 mb-6" data-testid="forecast-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("cba.forecast")}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{t("cba.growthHint")}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex border">
                <button onClick={() => setForecastMode("area")}
                  className={`px-3 h-8 text-[10px] font-bold uppercase tracking-widest ${forecastMode === "area" ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-muted"}`}
                  data-testid="forecast-mode-area">{t("cba.byArea")}</button>
                <button onClick={() => setForecastMode("family")}
                  className={`px-3 h-8 text-[10px] font-bold uppercase tracking-widest border-l ${forecastMode === "family" ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-muted"}`}
                  data-testid="forecast-mode-family">{t("cba.byFamily")}</button>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[10px] font-bold uppercase whitespace-nowrap">{t("cba.growthFactor")}</Label>
                <Input type="number" min="0.5" max="2" step="0.05" value={growth}
                  onChange={(e) => setGrowth(parseFloat(e.target.value) || 1.05)}
                  className="rounded-none h-8 text-xs w-24 tabular-nums" data-testid="forecast-growth-input" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="border p-3" data-testid="forecast-baseline">
              <div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("cba.baseline")}</div>
              <div className="text-2xl font-black tabular-nums mt-1">{fmtMoney(forecast.totals.baseline_forecast)}</div>
            </div>
            <div className="border p-3 border-primary" data-testid="forecast-projected">
              <div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("cba.projected")} <TrendingUp size={11} className="inline" /></div>
              <div className="text-2xl font-black tabular-nums text-primary mt-1">{fmtMoney(forecast.totals.projected_forecast)}</div>
            </div>
            <div className="border p-3" data-testid="forecast-delta">
              <div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("cba.growthDelta")}</div>
              <div className={`text-2xl font-black tabular-nums mt-1 ${forecast.totals.growth_delta > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                {forecast.totals.growth_delta >= 0 ? "+" : ""}{fmtMoney(forecast.totals.growth_delta)}
              </div>
            </div>
          </div>
          {(forecastMode === "area" ? forecast.by_area : forecast.by_family).length === 0 ? (
            <div className="text-[11px] text-muted-foreground py-4 text-center border border-dashed">Sin datos de consumo en la ventana seleccionada</div>
          ) : (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-2">
                {forecastMode === "area" ? t("cba.projByArea") : t("cba.projByFamily")}
              </div>
              <div className="tbl-wrap"><table className="tbl">
                <thead><tr>
                  <th>{forecastMode === "area" ? t("cba.area") : t("cba.family")}</th>
                  <th className="num">{t("cba.dailyAvg")}</th>
                  <th className="num">{t("cba.baseline")}</th>
                  <th className="num">{t("cba.projected")}</th>
                  <th className="num">{t("cba.share")}</th>
                </tr></thead>
                <tbody>
                  {(forecastMode === "area" ? forecast.by_area : forecast.by_family).map((a) => {
                    const key = forecastMode === "area" ? a.area : `${a.family_code} · ${a.family_name}`;
                    return (
                      <tr key={key} data-testid={`forecast-row-${forecastMode}-${forecastMode === "area" ? a.area : a.family_code}`}>
                        <td className="font-black uppercase text-xs">{key}</td>
                        <td className="num tabular-nums">{fmtMoney(a.daily_avg)}</td>
                        <td className="num tabular-nums">{fmtMoney(a.window_cost * (forecast.forecast_days / forecast.window_days))}</td>
                        <td className="num tabular-nums font-bold text-primary">{fmtMoney(a.projected_cost)}</td>
                        <td className="num tabular-nums">{a.share_pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
